import {useEffect, useState, useCallback, useRef} from 'react';
import { useChat } from '../context/Context';
import useHttp from './useHttp';
import {emitEventGA} from "../utils/emitEventGA";
import {mergeMessages, toMessagesList} from '../utils/mergeMessages';

export const useChatLifecycle = (setIsTokenChanged) => {
    const { widgetId, dialogId, setDialogId, messages, setMessages, isDialogActive, isDialogStateReady, setDialogActive, engineAutomations, widgetData:{ga4_code}} = useChat();

    const [messageText, setMessageText] = useState('');
    const [isPreload, setPreload] = useState(false);
    const [historyMessages, setHistoryMessages] = useState([]);

    const initialChat = useRef(false);
    const dialogRequestRef = useRef(null);

    const [dialogData, requestDialogId, , , setDialogData] = useHttp(`widget/${widgetId}/dialog`);
    const [messagesData, messagesRequest, , isLoading] = useHttp(
        `widget/${widgetId}/dialog/${dialogId?.data?.id}`
    );
    const [historyData, historyRequest] = useHttp(
        `widget/${widgetId}/dialog/${dialogId?.data?.id}?preload=1`
    );
    const [, sendMessageRequest, error] = useHttp(
        `widget/${widgetId}/dialog/${dialogId?.data?.id}?text=${encodeURIComponent(messageText)}`
    );
    const [, closeDialog] = useHttp(`widget/${widgetId}/dialog/${dialogId?.data?.id}/close`);

    const firstMsgNotifiedRef = useRef(false);

    const fetchDialogId = useCallback((options = {method: 'POST'}) => {
        if (dialogId?.data?.id) return Promise.resolve(dialogId);

        if (!dialogRequestRef.current) {
            dialogRequestRef.current = requestDialogId(options).then((response) => {
                if (!response?.data?.id) {
                    dialogRequestRef.current = null;
                }

                return response;
            });
        }

        return dialogRequestRef.current;
    }, [dialogId, requestDialogId]);

    useEffect(() => {
        if (dialogId?.data?.id) {
            dialogRequestRef.current = null;
        }
    }, [dialogId?.data?.id]);

    useEffect(() => {
        if (isDialogStateReady && isDialogActive && !dialogId?.data?.id) {
            fetchDialogId({ method: 'POST' });
        }
    }, [isDialogStateReady, isDialogActive, dialogId?.data?.id, fetchDialogId]);

    useEffect(() => {
        if (dialogData?.data?.id) {
            setDialogId(dialogData);
        }
    }, [dialogData]);

    useEffect(() => {
        if (dialogId?.data?.id && isDialogActive) {
            messagesRequest();
        }
    }, [dialogId?.data?.id, isDialogActive, messagesRequest]);

    useEffect(() => {
        if (!messageText || !dialogId?.data?.id) return;

        (async () => {
            try {
                const response = await sendMessageRequest({ method: 'POST' });
                if (!response) return;

                localStorage.setItem('dialogStatus', 'active');
                setDialogActive(true);
                messagesRequest();

                engineAutomations?.notifyLastMessage();

                if (!firstMsgNotifiedRef.current) {
                    engineAutomations?.notifyFirstMessage();
                    firstMsgNotifiedRef.current = true;
                }

                setMessageText('');
            } catch (e) {
                console.error(e)
            }
        })();
    }, [messageText, dialogId?.data?.id, sendMessageRequest, engineAutomations, setDialogActive, messagesRequest]);

    useEffect(() => {
        if (error && setIsTokenChanged) {
            setIsTokenChanged(prev => !prev);
        }
    }, [error]);

    useEffect(() => {
        if (isPreload && dialogId?.data?.id) {
            historyRequest();
        }
    }, [isPreload, dialogId?.data?.id]);

    useEffect(() => {
        if (isPreload) return;

        setHistoryMessages((prev) => (prev.length ? [] : prev));
    }, [isPreload]);

    useEffect(() => {
        if (!historyData?.data) return;

        setHistoryMessages(
            toMessagesList(historyData.data).map((msg) => ({ ...msg, isHistory: true }))
        );
    }, [historyData]);

    useEffect(() => {
        setMessages(mergeMessages(historyMessages, toMessagesList(messagesData?.data)));
    }, [messagesData, historyMessages, setMessages]);

    const preloadMessagesHandler = useCallback(() => {
        setTimeout(() => setPreload(true), 10);
    }, []);

    const sendMessage = useCallback(async (text) => {
        if (!initialChat.current) {
            initialChat.current = true;
            emitEventGA('client_initiate_chat', {}, ga4_code);
        }

        if (!text?.trim()) return;
        if (!dialogId?.data?.id) {
            await fetchDialogId({ method: 'POST' });
        }
        setMessageText(text);
    }, [dialogId]);

    return {
        dialogId,
        fetchDialogId,
        messages,
        setDialogData,
        isPreload,
        setPreload,
        preloadMessagesHandler,
        sendMessage,
        sendMessageRequest,
        messagesRequest,
        isLoading,
        messagesData,
        closeDialog,
    };
};
